/**
 * Created by mac on 12/1/24
 */

// How a purchase reaches the player when the store hands its receipt back later, a restore:
//   find(context)            the seller of the goods: an offer, a mission, a map block; nothing means it is gone
//   isOwned(entity, context) the goods are already there, the receipt closes and gives nothing, on purpose
//   give(entity, context)    the seller's own success handler; a window it opens goes on top of the restore focus
//   location                 where the restore may run: "main", a location id, or a function of (product, context)
//   boughtReward(context)    the reward stashed into the purchase context at buy time, the fallback when the seller is gone
// Restorable.resolveOutcome decides, PaymentsLogic.showRestore runs the decision; a receipt bound to another
// active location is not left waiting, PaymentsLogic.travelToRestore tells the player and takes them there.
// listActions is the previous hook, kept for the sales whose goods are granted by their windows.
const Restorable = function (options) {
    this.options = options || {};
};

Restorable.prototype.waitGoodMoment = function (product, context) {
    return !cleverapps.environment.isMainScene() || Boolean(this.pendingLocation(product, context));
};

// the active location the receipt is bound to while the player is elsewhere; undefined when the restore can run here
Restorable.prototype.pendingLocation = function (product, context) {
    const required = context.location || this.requiredLocation(product, context);
    const selected = cleverapps.meta.getSelectedLocation();

    if (required === "main") {
        return selected.locationConfig.isMain ? undefined : cleverapps.meta.getMainLocationId();
    }

    if (required && required !== selected.locationId && Restorable.IsActiveLocation(required)) {
        return required;
    }

    return undefined;
};

Restorable.IsActiveLocation = function (location) {
    return cleverapps.meta.listActiveLocations().indexOf(location) !== -1;
};

Restorable.prototype.requiredLocation = function (product, context) {
    let location = this.options.location;

    if (typeof location === "function") {
        location = location(product, context);
    }

    return location;
};

Restorable.prototype.listActions = function (product, context, consume) {
    const listActions = this.options.listActions;
    return listActions && listActions(product, context, consume);
};

Restorable.prototype.find = function (context) {
    return this.options.find && this.options.find(context);
};

Restorable.prototype.isOwned = function (entity, context) {
    return Boolean(this.options.isOwned && this.options.isOwned(entity, context));
};

Restorable.prototype.give = function (entity, context) {
    this.options.give(entity, context);
};

Restorable.prototype.getBoughtReward = function (context) {
    const boughtReward = this.options.boughtReward;
    return boughtReward && boughtReward(context);
};

// what a restore delivers: the seller's goods, nothing for goods already owned, else the reward stashed at buy,
// the product's restore reward, the product's reward, or nothing
Restorable.resolveOutcome = function (restorable, product, context) {
    const entity = restorable.find(context);

    if (entity && restorable.isOwned(entity, context)) {
        return { outcome: "already_owned" };
    }

    if (entity) {
        return { outcome: "given", entity };
    }

    if (context.reward) {
        return { outcome: "context_reward", reward: context.reward };
    }

    if (product.restoreReward) {
        return { outcome: "restore_reward", reward: product.restoreReward };
    }

    if (product.reward) {
        return { outcome: "product_reward", reward: product.reward };
    }

    return { outcome: "no_reward" };
};

// the restore hands the focus back right after give; a handler that opens a bare window opens it on top
Restorable.displayWindow = function (focus, action) {
    cleverapps.focusManager.display({
        focus,
        stack: true,
        action: (f) => {
            action();
            cleverapps.focusManager.onceNoWindowsListener = f;
        }
    });
};

// the player already owns the goods - consume the receipt and give nothing, on purpose
Restorable.AlreadyOwnedActions = function (consume) {
    return [
        function (f) {
            consume("already_owned");
            f();
        }
    ];
};

Restorable.LocationContextExpedition = function (product, context) {
    return context.expedition || "main";
};

Restorable.LocationOfferExpedition = function (product, context) {
    return cleverapps.offerManager.getOfferExpedition(context.offer);
};

Restorable.LocationMission = function (product, context) {
    return Missions[context.mission] && Missions[context.mission].location || "main";
};

// the sellers most restorables share
Restorable.FindOffer = function (context) {
    return cleverapps.offerManager.findOffer({ type: context.offer });
};

Restorable.GiveOffer = function (offer, context) {
    offer.logic.processBought(true, context.reward);
};

Restorable.FindRunningMission = function (context) {
    const mission = cleverapps.missionManager.findByType(context.mission);
    return mission && mission.isRunning() ? mission : undefined;
};

Restorable.BY_TYPE = {};

Restorable.BY_TYPE.default = new Restorable();
